// src/features/delegations/pages/DelegationDetailPage/tabs/InfoTab.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Divider, Tooltip, Typography } from '@mui/material';
import {
  Group as PartiesIcon,
  CalendarMonth as ValidityIcon,
  Description as ContentIcon,
  CheckCircle as SignedIcon,
  RadioButtonUnchecked as UnsignedIcon,
} from '@mui/icons-material';
import StaffMember from '../../../../../components/common/StaffMember';
import DateWithHint from '../../../../../components/common/DateWithHint';
import SectionHeader from '../../../../../components/common/SectionHeader';

function InfoRow({ label, value }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4 }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mt: 0.5, lineHeight: 1.7 }}>
        {value || '—'}
      </Typography>
    </Box>
  );
}

function SignBadge({ hasSigned, t }) {
  return hasSigned ? (
    <Tooltip title={t('delegations:detail.signed')}>
      <SignedIcon fontSize="small" color="success" />
    </Tooltip>
  ) : (
    <Tooltip title={t('delegations:detail.unsigned')}>
      <UnsignedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
    </Tooltip>
  );
}

export default function InfoTab({ delegation }) {
  const { t } = useTranslation(['delegations', 'common']);
  const revision = delegation?.current_revision;

  const hasSigned = (staffMember) =>
    revision?.signatures?.some((s) => s.staff_member?.staff_member_id === staffMember?.staff_member_id) ?? false;

  return (
    <Box sx={{ p: 4, display: 'flex', flexDirection: 'column', gap: 5 }}>

      {/* Top row: Parties + Validity */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 4 }}>

        {/* Parties */}
        <Box>
          <SectionHeader icon={PartiesIcon} title={t('delegations:detail.section.parties')} />
          <Box display="flex" flexWrap="wrap" gap={3}>
            <Box flex="1">
              <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
                {t('delegations:detail.fields.delegator')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5}>
                <StaffMember staffMember={delegation.delegator} nameVariant="body2" avatarSize={32} gap={1} />
                {revision && <SignBadge hasSigned={hasSigned(delegation.delegator)} t={t} />}
              </Box>
            </Box>
            <Box flex="1">
              <Typography variant="caption" color="text.secondary" fontWeight="medium" display="block" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, mb: 0.75 }}>
                {t('delegations:detail.fields.recipient')}
              </Typography>
              <Box display="flex" alignItems="center" gap={1.5}>
                <StaffMember staffMember={delegation.recipient} nameVariant="body2" avatarSize={32} gap={1} />
                {revision && <SignBadge hasSigned={hasSigned(delegation.recipient)} t={t} />}
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Validity */}
        {revision && (
          <Box>
            <SectionHeader icon={ValidityIcon} title={t('delegations:detail.section.validity')} />
            <Box display="flex" flexWrap="wrap" gap={3}>
              <Box flex="1">
                <DateWithHint
                  date={revision.issued_date}
                  label={t('delegations:create.fields.issued_date')}
                  highlightOverdue={false}
                  highlightUpcoming={false}
                />
              </Box>
              <Box flex="1">
                <DateWithHint
                  date={revision.review_date}
                  label={t('delegations:create.fields.review_date')}
                  upcomingThreshold={30}
                />
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* Content */}
      {revision && (
        <Box>
          <SectionHeader icon={ContentIcon} title={t('delegations:detail.section.content')} />
          <Box display="flex" flexDirection="column" gap={3}>
            <InfoRow label={t('delegations:create.fields.task_description')} value={revision.task_description} />
            <Divider />
            <InfoRow label={t('delegations:create.fields.steps')} value={revision.steps} />
            <Divider />
            <InfoRow label={t('delegations:create.fields.competency_requirements')} value={revision.competency_requirements} />
            <Divider />
            <InfoRow label={t('delegations:create.fields.contraindications')} value={revision.contraindications} />
            <Divider />
            <InfoRow label={t('delegations:create.fields.supervision')} value={revision.supervision} />
          </Box>
        </Box>
      )}

    </Box>
  );
}
